import { CLUSTER_MAX_ZOOM, clusterPoints } from "@/components/guide-public/guide-map-clusters";

/**
 * What the Guide's maps share once Mapbox is on the page (browser only, no
 * React): gathering overlapping pins into clusters, and the callout card a
 * selected pin shows. Typed against the few Mapbox methods used, so the
 * maps' lazy import stays the only place that knows the library.
 */

export type LngLat = [number, number];

export type EngineMap = {
  project: (lngLat: LngLat) => { x: number; y: number };
  unproject: (point: [number, number]) => { lng: number; lat: number };
  getZoom: () => number;
  on: (event: string, fn: () => void) => void;
  off: (event: string, fn: () => void) => void;
  fitBounds: (b: [[number, number], [number, number]], o?: Record<string, unknown>) => void;
};

export type EngineMarker = {
  getElement: () => HTMLElement;
  getLngLat: () => { lng: number; lat: number };
  remove: () => void;
};

export type MarkerClass = new (opts: { element: HTMLElement; anchor?: string }) => {
  setLngLat: (lngLat: LngLat) => { addTo: (map: unknown) => EngineMarker };
};

export type ClusterEngine = { refresh: () => void; dispose: () => void };

/**
 * Keeps the map readable as the selection grows: at each pause of the map,
 * pins that would overlap are hidden behind one cluster marker with their
 * count; clicking it zooms to them. `shown` says which pins the page's
 * filters allow; the engine owns every pin's visibility from then on.
 */
export function attachClusters(opts: {
  map: EngineMap;
  Marker: MarkerClass;
  markers: Map<string, EngineMarker>;
  shown: () => Set<string>;
  /** Extra class for the cluster markers (the studio's are ivory). */
  className?: string;
  /** Accessible name for a cluster of `count` pins. */
  label: (count: number) => string;
}): ClusterEngine {
  const { map, Marker, markers } = opts;
  let clusters: EngineMarker[] = [];

  const refresh = () => {
    for (const c of clusters) c.remove();
    clusters = [];
    const keys = opts.shown();
    const show = (key: string, on: boolean) => {
      const el = markers.get(key)?.getElement();
      if (el) el.style.display = on ? "" : "none";
    };
    if (map.getZoom() >= CLUSTER_MAX_ZOOM) {
      markers.forEach((_, key) => show(key, keys.has(key)));
      return;
    }
    const points = [...keys].flatMap((key) => {
      const marker = markers.get(key);
      if (!marker) return [];
      const { lng, lat } = marker.getLngLat();
      const p = map.project([lng, lat]);
      return [{ key, x: p.x, y: p.y }];
    });
    const groups = clusterPoints(points);
    const clustered = new Set<string>();
    for (const group of groups) {
      if (group.keys.length < 2) continue;
      for (const key of group.keys) clustered.add(key);
      const el = document.createElement("button");
      el.type = "button";
      el.className = `eg-cluster${opts.className ? ` ${opts.className}` : ""}`;
      el.textContent = String(group.keys.length);
      el.setAttribute("aria-label", opts.label(group.keys.length));
      el.addEventListener("click", (event) => {
        event.stopPropagation();
        const members = group.keys.map((key) => markers.get(key)!.getLngLat());
        const lngs = members.map((m) => m.lng);
        const lats = members.map((m) => m.lat);
        map.fitBounds(
          [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
          ],
          { padding: 80, maxZoom: 17, duration: 600 },
        );
      });
      const { lng, lat } = map.unproject([group.x, group.y]);
      clusters.push(new Marker({ element: el }).setLngLat([lng, lat]).addTo(map));
    }
    markers.forEach((_, key) => show(key, keys.has(key) && !clustered.has(key)));
  };

  map.on("moveend", refresh);
  refresh();
  return {
    refresh,
    dispose: () => {
      map.off("moveend", refresh);
      for (const c of clusters) c.remove();
      clusters = [];
    },
  };
}

export type CalloutInput = {
  title: string;
  meta?: string | null;
  image?: string | null;
  href?: string | null;
  linkLabel?: string | null;
  external?: boolean;
  dir?: "ltr" | "rtl";
  /** Further links under the main one (directions, the official site). */
  extraLinks?: Array<{ href: string; label: string; external?: boolean }>;
};

/** The card a selected pin shows, built from plain text (never markup). */
export function calloutElement(input: CalloutInput): HTMLElement {
  const root = document.createElement("div");
  root.className = "eg-callout";
  root.dir = input.dir ?? "ltr";
  if (input.image) {
    const img = document.createElement("img");
    img.className = "eg-callout__photo";
    img.src = input.image;
    img.alt = "";
    root.appendChild(img);
  }
  const body = document.createElement("div");
  body.className = "eg-callout__body";
  const title = document.createElement(input.href ? "a" : "span");
  title.className = "eg-callout__title";
  title.textContent = input.title;
  if (input.href && title instanceof HTMLAnchorElement) {
    title.href = input.href;
    if (input.external) {
      title.target = "_blank";
      title.rel = "noopener noreferrer";
    }
  }
  body.appendChild(title);
  if (input.meta) {
    const meta = document.createElement("span");
    meta.className = "eg-callout__meta";
    meta.textContent = input.meta;
    body.appendChild(meta);
  }
  if (input.href && input.linkLabel) {
    const link = document.createElement("a");
    link.className = "eg-callout__link";
    link.href = input.href;
    link.textContent = `${input.linkLabel} →`;
    if (input.external) {
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }
    body.appendChild(link);
  }
  for (const extra of input.extraLinks ?? []) {
    const link = document.createElement("a");
    link.className = "eg-callout__link";
    link.href = extra.href;
    link.textContent = `${extra.label} →`;
    if (extra.external) {
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }
    body.appendChild(link);
  }
  root.appendChild(body);
  return root;
}

/** The custom event the drawn Egypt sends when a region is picked; the real map answers it. */
export const REGION_PICK_EVENT = "localized:region";
export type RegionPickDetail = { slug: string; handled: boolean };
/** Sent by the real map whenever its region changes (chips, the picker, or a pick above), so the drawn Egypt follows. */
export const REGION_CHANGED_EVENT = "localized:region-changed";

/** The two looks a visitor can pick: the Guide's repainted streets, or satellite with streets. */
export const MAP_STYLES = {
  streets: "mapbox://styles/mapbox/light-v11",
  satellite: "mapbox://styles/mapbox/satellite-streets-v12",
} as const;
export type MapStyleKey = keyof typeof MAP_STYLES;

export type LngLatBounds = [[number, number], [number, number]];

type SpotlightMap = {
  getSource: (id: string) => { setData: (data: unknown) => void } | undefined;
  addSource: (id: string, source: Record<string, unknown>) => void;
  addLayer: (layer: Record<string, unknown>) => void;
  isStyleLoaded: () => boolean;
  on: (event: string, fn: () => void) => void;
  off: (event: string, fn: () => void) => void;
  once: (event: string, fn: () => void) => void;
};

export type Spotlight = { set: (bounds: LngLatBounds | null) => void; dispose: () => void };

const SPOTLIGHT_SOURCE = "eg-spotlight";

/** A ring around the whole world, clockwise, for the mask's outer edge. */
const WORLD: Array<[number, number]> = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

function spotlightData(bounds: LngLatBounds | null) {
  if (!bounds) return { type: "FeatureCollection", features: [] };
  const [[w, s], [e, n]] = bounds;
  // Counter-clockwise hole: the chosen region stays bright, the rest dims.
  const hole: Array<[number, number]> = [
    [w, s],
    [w, n],
    [e, n],
    [e, s],
    [w, s],
  ];
  return {
    type: "FeatureCollection",
    features: [
      { type: "Feature", properties: { part: "mask" }, geometry: { type: "Polygon", coordinates: [WORLD, hole] } },
      { type: "Feature", properties: { part: "edge" }, geometry: { type: "LineString", coordinates: hole } },
    ],
  };
}

/**
 * Keeps the chosen region in the light: everything outside it is washed in
 * a thin layer of ink and a dashed gold line runs round it. Survives a style
 * change (the layers are put back when the new style loads).
 */
export function attachSpotlight(map: SpotlightMap): Spotlight {
  let current: LngLatBounds | null = null;
  const install = () => {
    if (map.getSource(SPOTLIGHT_SOURCE)) {
      map.getSource(SPOTLIGHT_SOURCE)?.setData(spotlightData(current));
      return;
    }
    map.addSource(SPOTLIGHT_SOURCE, { type: "geojson", data: spotlightData(current) });
    map.addLayer({
      id: `${SPOTLIGHT_SOURCE}-mask`,
      type: "fill",
      source: SPOTLIGHT_SOURCE,
      filter: ["==", ["get", "part"], "mask"],
      paint: { "fill-color": "#16120e", "fill-opacity": 0.14 },
    });
    map.addLayer({
      id: `${SPOTLIGHT_SOURCE}-edge`,
      type: "line",
      source: SPOTLIGHT_SOURCE,
      filter: ["==", ["get", "part"], "edge"],
      paint: { "line-color": "#b8893b", "line-width": 2, "line-dasharray": [2, 2] },
    });
  };
  const onStyle = () => install();
  map.on("style.load", onStyle);
  if (map.isStyleLoaded()) install();
  else map.once("load", install);
  return {
    set: (bounds) => {
      current = bounds;
      map.getSource(SPOTLIGHT_SOURCE)?.setData(spotlightData(current));
    },
    dispose: () => map.off("style.load", onStyle),
  };
}

/** What a region's spotlight looks like, for tests: the mask has a hole where the region is. */
export { spotlightData };
